import React, { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-hot-toast';
import { Mail, X } from 'lucide-react';
import { functions } from '@/firebase';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { logger } from '@/utils/logger';

const DISMISS_KEY = 'verifyBannerDismissed';

/**
 * Slim, on-brand (deep teal / forest "primary" palette) SOFT NUDGE banner.
 *
 * Shown only when there is a signed-in user whose email is not yet verified.
 * - "Resend email" reuses the `sendCustomVerificationEmail` callable with a 60s
 *   cooldown and a success/error toast (react-hot-toast).
 * - The "×" dismiss hides the banner for the rest of the browser session via a
 *   sessionStorage flag.
 * - When the user later verifies (currentUser.emailVerified flips to true) the
 *   banner disappears automatically.
 *
 * Mounted inside <PrivateRoute /> so it appears across all authenticated pages
 * (dashboard / quiz / sims / flashcards / progress) and never on public or auth
 * pages.
 */
const VerifyEmailBanner: React.FC = () => {
  const { currentUser } = useEnhancedAuth();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [dismissed, setDismissed] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => {
      setResendCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!currentUser || resendCooldown > 0 || isResending) return;

    setIsResending(true);
    try {
      const sendCustomVerificationEmail = httpsCallable(functions, 'sendCustomVerificationEmail');
      await sendCustomVerificationEmail({});
      setResendCooldown(60);
      toast.success('Verification email sent. Check your inbox and spam folder.');
      logger.info('VerifyEmailBanner', 'Verification email resent via SendGrid', { userId: currentUser.uid });
    } catch (error) {
      logger.error('VerifyEmailBanner', 'Failed to resend verification email', { error });
      toast.error('Could not send the verification email. Please try again in a moment.');
    } finally {
      setIsResending(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, 'true');
    } catch {
      // sessionStorage may be unavailable (private mode); dismiss for this render anyway.
    }
  };

  // Only show for a signed-in, unverified user that hasn't dismissed this session.
  if (!currentUser || currentUser.emailVerified || dismissed) {
    return null;
  }

  return (
    <div className="bg-primary-50 border-b border-primary-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Mail className="h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" />
            <p className="text-sm leading-snug text-primary-800">
              Verify your email to secure your account
              {import.meta.env.VITE_USE_EMULATOR === 'true' && (
                <span className="ml-2 hidden sm:inline text-xs text-primary-600">
                  [DEV] verification link is in the emulator logs
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              type="button"
              onClick={handleResend}
              disabled={isResending || resendCooldown > 0}
              className="text-xs sm:text-sm font-semibold text-primary-700 hover:text-primary-800 underline-offset-2 hover:underline disabled:no-underline disabled:opacity-60 disabled:cursor-not-allowed min-h-[44px] px-2 rounded whitespace-nowrap transition-colors"
            >
              {isResending
                ? 'Sending…'
                : resendCooldown > 0
                  ? `Resend in ${resendCooldown}s`
                  : 'Resend email'}
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="flex h-11 w-11 items-center justify-center rounded text-primary-600 hover:text-primary-800 hover:bg-primary-100 transition-colors"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailBanner;
